// src/services/notificationService.js
import { authenticatedFetch } from './api';

const API_BASE_URL = '/notifications';

class NotificationCache {
    constructor() {
        this.cache = new Map();
        this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    }

    set(key, value) {
        this.cache.set(key, {
            value,
            timestamp: Date.now()
        });
    }

    get(key) {
        const cached = this.cache.get(key);
        if (!cached) return null;
        
        if (Date.now() - cached.timestamp > this.cacheDuration) {
            this.cache.delete(key);
            return null;
        }
        
        return cached.value;
    }

    invalidate(pattern) {
        if (!pattern) {
            this.cache.clear();
            return;
        }
        
        for (const key of this.cache.keys()) {
            if (key.includes(pattern)) {
                this.cache.delete(key);
            }
        }
    }
}

const cache = new NotificationCache();

const retryRequest = async (fn, maxRetries = 3, delay = 1000) => {
    let lastError;
    
    for (let i = 0; i < maxRetries; i++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            
            if (error.status >= 400 && error.status < 500) {
                throw error;
            }
            
            if (i < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
            }
        }
    }
    
    throw lastError;
};

export const getNotifications = async (params = {}) => {
    const cacheKey = `notifications_${JSON.stringify(params)}`;
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const query = new URLSearchParams(params).toString();
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}?${query}`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const markAsRead = async (id) => {
    const result = await authenticatedFetch(`${API_BASE_URL}/${id}/read`, { 
        method: 'PATCH' 
    });
    
    cache.invalidate('notifications_');
    return result;
};

export const markAllAsRead = async () => {
    const result = await authenticatedFetch(`${API_BASE_URL}/read-all`, { 
        method: 'POST' 
    });
    
    cache.invalidate('notifications_');
    return result;
};

export const deleteNotification = async (id) => {
    const result = await authenticatedFetch(`${API_BASE_URL}/${id}`, { 
        method: 'DELETE' 
    });
    
    cache.invalidate('notifications_');
    return result;
};

export const sendNotificationToUser = async (userId, title, body, data = {}) => {
    const endpoint = `${API_BASE_URL}/send/user/${userId}`;
    
    console.log(`[NotificationService] Sending notification to user ${userId} via endpoint: ${endpoint}`);
    
    const options = {
        method: 'POST',
        body: JSON.stringify({ title, body, data }),
    };
    
    return retryRequest(() => authenticatedFetch(endpoint, options));
};

export const sendNotificationToAllTechs = async (title, body, data = {}) => {
    const endpoint = `${API_BASE_URL}/send/techs`;
    
    console.log("[NotificationService] Sending notification to all techs via endpoint:", endpoint);
    
    const options = {
        method: 'POST',
        body: JSON.stringify({ title, body, data }),
    };
    
    return retryRequest(() => authenticatedFetch(endpoint, options));
};

export const sendNotification = async (notificationData) => {
    return retryRequest(() => 
        authenticatedFetch('/notifications/send', {
            method: 'POST',
            body: JSON.stringify(notificationData),
        })
    );
};

export const sendBroadcastToPublic = async (broadcastData) => {
    return retryRequest(() => 
        authenticatedFetch('/notifications/broadcast/public', {
            method: 'POST',
            body: JSON.stringify(broadcastData),
        })
    );
};

export const scheduleNotification = async (notificationData) => {
    const options = {
        method: 'POST',
        body: JSON.stringify(notificationData),
    };
    
    return retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/schedule`, options)
    );
};

export const getNotificationStats = async () => {
    const cacheKey = 'notification_stats';
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/stats`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const updateNotificationPreferences = async (preferences) => {
    const options = {
        method: 'PUT',
        body: JSON.stringify(preferences),
    };
    
    const result = await authenticatedFetch(`${API_BASE_URL}/preferences`, options);
    
    cache.invalidate('notification_preferences');
    return result;
};

export const getNotificationPreferences = async () => {
    const cacheKey = 'notification_preferences';
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/preferences`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const getGroupedNotifications = async () => {
    const cacheKey = 'notifications_grouped';
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/grouped`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const registerFcmToken = async (tokenData) => {
    const options = {
        method: 'POST',
        body: JSON.stringify(tokenData),
    };
    
    return authenticatedFetch(`${API_BASE_URL}/register-token`, options);
};

export const unregisterFcmToken = async (token) => {
    const options = {
        method: 'DELETE',
        body: JSON.stringify({ token }),
    };
    
    return authenticatedFetch(`${API_BASE_URL}/unregister-token`, options);
};

export const getNotificationById = async (id) => {
    const cacheKey = `notification_${id}`;
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/${id}`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const getNotificationsByDateRange = async (startDate, endDate) => {
    const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
    });
    
    return retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/date-range?${params}`)
    );
};

export const getUnreadCount = async () => {
    const cacheKey = 'unread_count';
    const cached = cache.get(cacheKey);
    
    if (cached !== null) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/unread-count`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const bulkMarkAsRead = async (notificationIds) => {
    const options = {
        method: 'POST',
        body: JSON.stringify({ ids: notificationIds }),
    };
    
    const result = await authenticatedFetch(`${API_BASE_URL}/bulk-read`, options);
    
    cache.invalidate('notifications_');
    cache.invalidate('unread_count');
    return result;
};

export const bulkDelete = async (notificationIds) => {
    const options = {
        method: 'POST',
        body: JSON.stringify({ ids: notificationIds }),
    };
    
    const result = await authenticatedFetch(`${API_BASE_URL}/bulk-delete`, options);
    
    cache.invalidate('notifications_');
    return result;
};

export const exportNotifications = async (format = 'json', params = {}) => {
    const query = new URLSearchParams({ ...params, format }).toString();
    
    return authenticatedFetch(`${API_BASE_URL}/export?${query}`);
};

export const testNotification = async (userId) => {
    const options = {
        method: 'POST',
        body: JSON.stringify({
            title: 'Test Notification',
            body: 'This is a test notification from FlowSense.',
            data: { type: 'test', timestamp: new Date().toISOString() }
        }),
    };
    
    return authenticatedFetch(`${API_BASE_URL}/test/${userId}`, options);
};

export const getNotificationTemplates = async () => {
    const cacheKey = 'notification_templates';
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/templates`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const createNotificationTemplate = async (templateData) => {
    const options = {
        method: 'POST',
        body: JSON.stringify(templateData),
    };
    
    const result = await authenticatedFetch(`${API_BASE_URL}/templates`, options);
    
    cache.invalidate('notification_templates');
    return result;
};

export const updateNotificationTemplate = async (id, templateData) => {
    const options = {
        method: 'PUT',
        body: JSON.stringify(templateData),
    };
    
    const result = await authenticatedFetch(`${API_BASE_URL}/templates/${id}`, options);
    
    cache.invalidate('notification_templates');
    return result;
};

export const deleteNotificationTemplate = async (id) => {
    const result = await authenticatedFetch(`${API_BASE_URL}/templates/${id}`, { 
        method: 'DELETE' 
    });
    
    cache.invalidate('notification_templates');
    return result;
};

export const getScheduledNotifications = async () => {
    const cacheKey = 'scheduled_notifications';
    const cached = cache.get(cacheKey);
    
    if (cached) {
        return cached;
    }
    
    const result = await retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/scheduled`)
    );
    
    cache.set(cacheKey, result);
    return result;
};

export const cancelScheduledNotification = async (id) => {
    const result = await authenticatedFetch(`${API_BASE_URL}/scheduled/${id}`, { 
        method: 'DELETE' 
    });
    
    cache.invalidate('scheduled_notifications');
    return result;
};

export const getNotificationDeliveryStatus = async (notificationId) => {
    return retryRequest(() => 
        authenticatedFetch(`${API_BASE_URL}/${notificationId}/delivery-status`)
    );
};

export const retryFailedNotification = async (notificationId) => {
    const options = {
        method: 'POST',
    };
    
    return authenticatedFetch(`${API_BASE_URL}/${notificationId}/retry`, options);
};

export const clearCache = () => {
    cache.invalidate();
    console.log('[NotificationService] Cache cleared');
};

export const invalidateCache = (pattern) => {
    cache.invalidate(pattern);
    console.log(`[NotificationService] Cache invalidated for pattern: ${pattern}`);
};

const notificationService = {
    getNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    sendNotificationToUser,
    sendNotificationToAllTechs,
    sendNotification,
    sendBroadcastToPublic,
    scheduleNotification,
    getNotificationStats,
    updateNotificationPreferences,
    getNotificationPreferences,
    getGroupedNotifications,
    registerFcmToken,
    unregisterFcmToken,
    getNotificationById,
    getNotificationsByDateRange,
    getUnreadCount,
    bulkMarkAsRead,
    bulkDelete,
    exportNotifications,
    testNotification,
    getNotificationTemplates,
    createNotificationTemplate,
    updateNotificationTemplate,
    deleteNotificationTemplate,
    getScheduledNotifications,
    cancelScheduledNotification,
    getNotificationDeliveryStatus,
    retryFailedNotification,
    clearCache,
    invalidateCache
};

export default notificationService;